'use client';

import { useTranslations } from 'next-intl';
import {
  Download,
  FileText,
  FileSpreadsheet,
  FileType,
  Loader2,
} from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { cn } from '@/shared/lib/cn';
import { useEstimateExport } from '../hooks/use-estimate-export';
import type { EstimateExportFormat } from '../api/estimate-service';

interface EstimateExportMenuProps {
  estimateId: string;
  /** Stop the click from bubbling to a parent card link/button. */
  stopPropagation?: boolean;
  className?: string;
  disabled?: boolean;
}

const EXPORT_FORMATS: ReadonlyArray<{
  format: EstimateExportFormat;
  // PDF/Excel/Text are file-type words, kept client-static (a fixed export
  // surface, not server-driven business copy) and localized.
  labelKey: 'pdf' | 'excel' | 'text';
  Icon: typeof FileText;
}> = [
  { format: 'pdf', labelKey: 'pdf', Icon: FileText },
  { format: 'xlsx', labelKey: 'excel', Icon: FileSpreadsheet },
  { format: 'txt', labelKey: 'text', Icon: FileType },
];

/**
 * The PDF / Excel / Text download items shared by every estimate export
 * surface: the card-corner dropdown below and the "Export estimate" submenu in
 * the concept page kebab (ConceptActions). Each item triggers the export
 * endpoint download and shows a per-format spinner while in flight. Only valid
 * for `ready` estimates — the caller gates on status.
 */
export function EstimateExportMenuItems({
  estimateId,
}: {
  estimateId: string;
}) {
  const t = useTranslations('common.estimate.export');
  const { exportEstimate, exportingFormat, isExporting } =
    useEstimateExport(estimateId);

  return (
    <>
      {EXPORT_FORMATS.map(({ format, labelKey, Icon }) => (
        <DropdownMenuItem
          key={format}
          disabled={isExporting}
          onSelect={(e) => {
            e.preventDefault();
            void exportEstimate(format);
          }}
        >
          {exportingFormat === format ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Icon className="h-4 w-4" />
          )}
          {t(labelKey)}
        </DropdownMenuItem>
      ))}
    </>
  );
}

/**
 * Compact icon-trigger export menu used on the estimate card corner overlay.
 * The concept page editor exports via the page kebab instead ("Export
 * estimate" submenu in ConceptActions). Only mounted for `ready` estimates
 * (export is invalid otherwise) — the caller gates on status.
 */
export function EstimateExportMenu({
  estimateId,
  stopPropagation = false,
  className,
  disabled = false,
}: EstimateExportMenuProps) {
  const t = useTranslations('common.estimate.export');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        onClick={
          stopPropagation ? (e) => e.stopPropagation() : undefined
        }
      >
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className={cn('h-8 w-8 rounded-full', className)}
          disabled={disabled}
          aria-label={t('label')}
        >
          <Download className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      >
        <EstimateExportMenuItems estimateId={estimateId} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
